import React, { useState } from "react";
import { motion } from "framer-motion";
import { Search, PackageSearch, MapPin, Truck, CheckCircle2, Circle, Loader2, Package } from "lucide-react";
import { PageHero } from "@/components/shared/PageHero";
import { Input } from "@/components/ui/input";
import { trackShipment, formatApiError } from "@/lib/api";

const statusColor = {
  "Booking Confirmed": "bg-blue-100 text-brand",
  "Packed": "bg-amber-100 text-amber-700",
  "In Transit": "bg-indigo-100 text-indigo-700",
  "Delivered": "bg-green-100 text-green-700",
};

export default function Tracking() {
  const [query, setQuery] = useState("");
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleTrack = async (e) => {
    e.preventDefault();
    if (!query.trim()) return;
    setLoading(true);
    setError("");
    setResult(null);
    try {
      const data = await trackShipment(query.trim());
      setResult(data);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Unable to track this shipment.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="tracking-page">
      <PageHero
        breadcrumb="Track"
        eyebrow="Shipment Tracking"
        title="Where's my move?"
        description="Enter your booking ID to see the live status of your shipment, every step of the way."
      />

      <section className="bg-surface py-16 lg:py-24 min-h-[60vh]">
        <div className="container-x max-w-4xl">
          {/* Search */}
          <form onSubmit={handleTrack} className="rounded-3xl bg-white border border-gray-100 shadow-card p-6 sm:p-8" data-testid="tracking-form">
            <label htmlFor="track-input" className="block font-heading font-bold text-xl text-ink mb-4">
              Track your shipment
            </label>
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative flex-1">
                <PackageSearch className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                <Input
                  id="track-input"
                  data-testid="tracking-input"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Enter booking ID (e.g. ASP-100001)"
                  className="h-14 pl-12 rounded-xl bg-surface text-base"
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                data-testid="tracking-submit"
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-brand text-white font-semibold px-8 h-14 hover:bg-brand-deep transition-colors disabled:opacity-70"
              >
                {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Search className="h-5 w-5" />}
                Track
              </button>
            </div>
            <p className="mt-3 text-sm text-muted-foreground">
              Try sample IDs: <button type="button" onClick={() => setQuery("ASP-100001")} className="font-semibold text-brand hover:underline">ASP-100001</button> or <button type="button" onClick={() => setQuery("ASP-100002")} className="font-semibold text-brand hover:underline">ASP-100002</button>
            </p>
          </form>

          {error && (
            <motion.div
              initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
              className="mt-6 rounded-2xl border border-red-100 bg-red-50 p-5 text-red-700 flex items-center gap-3"
              data-testid="tracking-error"
            >
              <Package className="h-5 w-5" /> {error}
            </motion.div>
          )}

          {result && (
            <motion.div
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
              className="mt-8 rounded-3xl bg-white border border-gray-100 shadow-card overflow-hidden"
              data-testid="tracking-result"
            >
              {/* header */}
              <div className="p-6 sm:p-8 border-b border-gray-100">
                <div className="flex flex-wrap items-center justify-between gap-4">
                  <div>
                    <p className="text-sm text-muted-foreground">Booking ID</p>
                    <p className="font-heading font-extrabold text-2xl text-ink" data-testid="result-booking-id">{result.booking_id}</p>
                  </div>
                  <span className={`px-4 py-2 rounded-full text-sm font-semibold ${statusColor[result.status] || "bg-gray-100 text-gray-700"}`} data-testid="result-status">
                    {result.status}
                  </span>
                </div>

                <div className="mt-6 grid sm:grid-cols-3 gap-4 text-sm">
                  <div className="flex items-center gap-2"><MapPin className="h-4 w-4 text-brand-accent" /><span className="text-muted-foreground">From: <span className="text-ink font-medium">{result.origin}</span></span></div>
                  <div className="flex items-center gap-2"><MapPin className="h-4 w-4 text-brand-accent" /><span className="text-muted-foreground">To: <span className="text-ink font-medium">{result.destination}</span></span></div>
                  <div className="flex items-center gap-2"><Truck className="h-4 w-4 text-brand-accent" /><span className="text-muted-foreground">ETA: <span className="text-ink font-medium">{result.estimated_delivery}</span></span></div>
                </div>

                {/* progress bar */}
                <div className="mt-6">
                  <div className="h-2.5 w-full rounded-full bg-gray-100 overflow-hidden">
                    <motion.div
                      className="h-full rounded-full bg-brand-accent"
                      initial={{ width: 0 }}
                      animate={{ width: `${result.progress}%` }}
                      transition={{ duration: 0.9, ease: "easeOut" }}
                      data-testid="result-progress"
                    />
                  </div>
                  <p className="mt-2 text-xs text-muted-foreground">{result.progress}% complete</p>
                </div>
              </div>

              {/* timeline */}
              <div className="p-6 sm:p-8">
                <ol className="relative">
                  {result.timeline.map((step, i) => {
                    const isLast = i === result.timeline.length - 1;
                    return (
                      <li key={step.stage} className="relative flex gap-4 pb-8 last:pb-0" data-testid={`timeline-${step.stage.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                        {!isLast && (
                          <span className={`absolute left-[15px] top-8 h-full w-0.5 ${step.completed ? "bg-brand-accent" : "bg-gray-200"}`} />
                        )}
                        <span className={`relative z-10 flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${step.completed ? "bg-brand-accent text-white" : "bg-gray-100 text-gray-400"} ${step.current ? "ring-4 ring-brand-accent/20" : ""}`}>
                          {step.completed ? <CheckCircle2 className="h-5 w-5" /> : <Circle className="h-4 w-4" />}
                        </span>
                        <div className="pt-1">
                          <p className={`font-heading font-bold ${step.current ? "text-brand" : step.completed ? "text-ink" : "text-muted-foreground"}`}>
                            {step.stage}
                          </p>
                          {step.note && <p className="mt-1 text-sm text-muted-foreground">{step.note}</p>}
                          {step.date && <p className="mt-0.5 text-xs text-muted-foreground">{step.date}</p>}
                        </div>
                      </li>
                    );
                  })}
                </ol>
              </div>
            </motion.div>
          )}
        </div>
      </section>
    </div>
  );
}
